import { srcSetFor, SIZES } from "../../lib/img";
import React, { useEffect, useRef, useState } from "react";
import { useLocale, useL } from "../../i18n/LocaleContext";
import SEO from "../../components/SEO";
import SiteHeader from "../../components/SiteHeader";
import {
  japaneseMentalArithmeticServiceSchema,
  breadcrumbSchema,
} from "../../data/schemas";
import "../Home.css";
import "../Programs.css";
import "./ProgramDetail.css";

/* ── Icons ── */
const CHECK = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
/* Points DOWN, so it is never tagged .i-arrow — down arrows do not
   mirror in right-to-left. */
const ICON_ARROW_DOWN = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <polyline points="6 13 12 19 18 13"/>
  </svg>
);
const ICON_CLOCK = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9"/>
    <polyline points="12 7 12 12 15.5 14"/>
  </svg>
);
const ICON_PEOPLE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const ICON_PIN = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);
const ICON_TROPHY = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 21h8"/>
    <path d="M12 17v4"/>
    <path d="M6 4h12v5a6 6 0 0 1-12 0V4z"/>
    <path d="M6 6H3v2a3 3 0 0 0 3 3"/>
    <path d="M18 6h3v2a3 3 0 0 1-3 3"/>
  </svg>
);
const ICON_ABACUS = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="1"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
    <circle cx="7"  cy="7"  r="1.2"/>
    <circle cx="12" cy="7"  r="1.2"/>
    <circle cx="17" cy="7"  r="1.2"/>
    <circle cx="8"  cy="14" r="1.2"/>
    <circle cx="12" cy="14" r="1.2"/>
    <circle cx="16" cy="14" r="1.2"/>
    <circle cx="10" cy="17" r="1.2"/>
    <circle cx="14" cy="17" r="1.2"/>
  </svg>
);
const ICON_BRAIN = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.5 2a2.5 2.5 0 0 1 5 0"/>
    <path d="M12 2v4"/>
    <path d="M4 8a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v1a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V8z"/>
    <path d="M12 12v10"/>
    <path d="M8 22h8"/>
  </svg>
);
const ICON_BOLT = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);
const ICON_EYE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const ICON_CERT = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="9" r="6"/>
    <polyline points="8 14 6 22 12 19 18 22 16 14"/>
  </svg>
);

/* ── Data ──
   Every text field is a locale triple, resolved through useL(). The
   glossary transliterations are kept: soroban → سوروبان / соробан,
   anzan → الأنزان / анзан. */
const STAGES = [
  { icon: ICON_ABACUS,
    title: { en: "The Physical Abacus", ar: "المعداد الحقيقي", ru: "Физический абакус" },
    desc:  { en: "Children learn to move the beads. Correct finger technique. Four operations, small to large.",
             ar: "يتعلّم الأطفال تحريك الخرزات. تقنية أصابع صحيحة. العمليات الأربع، من الصغيرة إلى الكبيرة.",
             ru: "Дети учатся двигать бусины. Правильная техника пальцев. Четыре действия, от малых к большим." } },
  { icon: ICON_EYE,
    title: { en: "The Mental Picture", ar: "الصورة الذهنية", ru: "Мысленный образ" },
    desc:  { en: "They begin to see the soroban in their mind. The bridge to anzan, calculating without touching an abacus.",
             ar: "يبدأون برؤية السوروبان في أذهانهم. الجسر إلى الأنزان، الحساب دون لمس المعداد.",
             ru: "Они начинают видеть соробан в уме. Мост к анзану, счёт без прикосновения к абакусу." } },
  { icon: ICON_BOLT,
    title: { en: "Speed Drills", ar: "تدريبات السرعة", ru: "Упражнения на скорость" },
    desc:  { en: "Timed sequences and flash anzan build automatic recall and the calm speed the method is known for.",
             ar: "سلاسل موقوتة وفلاش أنزان تبني الاستدعاء التلقائي والسرعة الهادئة التي تشتهر بها الطريقة.",
             ru: "Серии на время и флеш-анзан формируют автоматический счёт и ту спокойную скорость, которой известен метод." } },
  { icon: ICON_BRAIN,
    title: { en: "Focus and Memory", ar: "التركيز والذاكرة", ru: "Концентрация и память" },
    desc:  { en: "Working memory and sustained concentration are trained directly. This is where the transfer beyond math begins.",
             ar: "تُدرّب الذاكرة العاملة والتركيز المستمر بشكل مباشر. من هنا يبدأ الأثر خارج الرياضيات.",
             ru: "Рабочая память и устойчивая концентрация тренируются напрямую. Здесь начинается перенос за пределы математики." } },
];

const OUTCOMES = [
  { en: "Confident mental math across all four operations",
    ar: "حساب ذهني واثق في العمليات الأربع كلها",
    ru: "Уверенный устный счёт во всех четырёх действиях" },
  { en: "The foundations of anzan, calculating with no abacus",
    ar: "أساسيات الأنزان، الحساب دون معداد",
    ru: "Основы анзана, счёт без абакуса" },
  { en: "Stronger focus, working memory, and patience",
    ar: "تركيز وذاكرة عاملة وصبر أقوى",
    ru: "Сильнее концентрация, рабочая память и терпение" },
  { en: "A live Showcase demonstration and level certification",
    ar: "عرض مباشر في حفل Showcase وشهادة مستوى",
    ru: "Живая демонстрация на Showcase и сертификат уровня" },
];

const WHO_ITEMS = [
  { en: "The child who loses attention within minutes",
    ar: "الطفل الذي يفقد انتباهه في دقائق",
    ru: "Ребёнок, который теряет внимание за минуты" },
  { en: "The child who is quick but rushes and makes small errors",
    ar: "الطفل السريع لكنه يتعجّل ويقع في أخطاء صغيرة",
    ru: "Ребёнок, который считает быстро, но спешит и делает мелкие ошибки" },
  { en: "The strong maths student who is ready for real depth",
    ar: "الطفل القوي في الرياضيات والمستعد لعمق حقيقي",
    ru: "Сильный в математике ребёнок, готовый к настоящей глубине" },
  { en: "The child raised on screens who has never had to sit with a single task",
    ar: "الطفل الذي نشأ على الشاشات ولم يعتد الجلوس مع مهمة واحدة",
    ru: "Ребёнок, выросший на экранах, которому не приходилось оставаться с одной задачей" },
];

/* The hero H1 carries the gold <em> inside the program name, and the
   name's word order and natural break differ per language, so each
   language owns its own markup instead of a split string. */
const HERO_TITLES = {
  en: (<>Japanese <em>Mental</em><br />Arithmetic</>),
  ar: (<>الحساب <em>الذهني</em><br />الياباني</>),
  ru: (<>Японский <em>устный</em> счёт</>),
};

/* ── Nav ── */

/* ── Page ── */
export default function JapaneseMentalArithmetic() {
  const { t, lp, locale } = useLocale();
  const stages = useL(STAGES);
  const outcomes = useL(OUTCOMES);
  const whoItems = useL(WHO_ITEMS);
  const rootRef = useRef(null);
  const [nearBottom, setNearBottom] = useState(false);

  useEffect(() => {
    const els = rootRef.current
      ? Array.from(rootRef.current.querySelectorAll(".reveal"))
      : [];
    if (!("IntersectionObserver" in window) || els.length === 0) {
      els.forEach((el) => el.classList.add("in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 120;
      setNearBottom(atBottom);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToNext = () => {
    const nav = document.querySelector(".page-nav");
    const navH = nav ? nav.offsetHeight : 76;
    const targets = Array.from(
      document.querySelectorAll(".prog-content > .el-section, .el-cta")
    );
    const next = targets.find((el) => el.getBoundingClientRect().top > navH + 24);
    if (next) {
      const top = Math.round(next.getBoundingClientRect().top + window.scrollY - navH);
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="prog-page prog-rich" ref={rootRef}>
      <SEO
        title={t({
          en: "Japanese Mental Arithmetic (Soroban) in Dubai | Kidsology",
          ar: "الحساب الذهني الياباني (سوروبان) في دبي | Kidsology",
          ru: "Японский устный счёт (соробан) в Дубае | Kidsology",
        })}
        description={t({
          en: "An 8-session Japanese Mental Arithmetic (soroban) program in Dubai for ages 6 to 10, taught by a Soroban World Champion. KHDA and MOE approved.",
          ar: "برنامج الحساب الذهني الياباني (سوروبان) في دبي من 8 جلسات للأعمار من 6 إلى 10 سنوات، يقدّمه بطل العالم في السوروبان. معتمد من KHDA ووزارة التربية والتعليم.",
          ru: "Японский устный счёт (соробан) в Дубае: 8 занятий для детей от 6 до 10 лет, преподаёт чемпион мира по соробану. Одобрено KHDA и Министерством образования.",
        })}
        path="/programs/japanese-mental-arithmetic"
        image="/images/session-13.jpg"
        schema={[
          japaneseMentalArithmeticServiceSchema(locale),
          breadcrumbSchema([
            { name: { en: "Home",     ar: "الرئيسية", ru: "Главная" },   path: "/" },
            { name: { en: "Programs", ar: "البرامج",  ru: "Программы" }, path: "/programs" },
            { name: { en: "Japanese Mental Arithmetic",
                      ar: "نظام الحساب الذهني الياباني (سوروبان)",
                      ru: "Японский устный счёт (соробан)" },
              path: "/programs/japanese-mental-arithmetic" },
          ], locale),
        ]}
      />
      <SiteHeader />
      <main id="main">

      {/* 1 ── HERO */}
      <header className="el-hero">
        <div className="el-hero-left">
          <span className="el-hero-tag">
            {t({
              en: "Group · 8 Sessions · Ages 6 to 10",
              ar: "جماعي · 8 جلسات · من 6 إلى 10 سنوات",
              ru: "Группа · 8 занятий · от 6 до 10 лет",
            })}
          </span>
          <h1 className="el-hero-title el-display">
            {HERO_TITLES[locale] || HERO_TITLES.en}
          </h1>
          <p className="el-hero-headline">
            {t({
              en: "The original Japanese soroban method, taught the way it is taught in Japan.",
              ar: "طريقة السوروبان اليابانية الأصلية، تُدرّس كما تُدرّس في اليابان.",
              ru: "Оригинальный японский метод соробан, который преподают так, как его преподают в Японии.",
            })}
          </p>
          <p className="el-hero-body">
            {t({
              en: "An eight session programme built to train real mental math, and the focus and memory that come with it. Sixty minutes per session, in person in Dubai. KHDA and MOE approved for 2026/27.",
              ar: "برنامج من ثماني جلسات مبني لتدريب الحساب الذهني الحقيقي، وما يرافقه من تركيز وذاكرة. ستون دقيقة للجلسة، حضورياً في دبي. معتمد من هيئة المعرفة والتنمية البشرية (KHDA) ووزارة التربية والتعليم لعام 2026/27.",
              ru: "Программа из восьми занятий, построенная, чтобы тренировать настоящий устный счёт, а вместе с ним концентрацию и память. Шестьдесят минут на занятие, лично в Дубае. Одобрено KHDA и Министерством образования ОАЭ на 2026/27.",
            })}
          </p>
          <div className="el-not-badges">
            <span className="el-not-badge">{t({ en: "8 sessions", ar: "8 جلسات", ru: "8 занятий" })}</span>
            <span className="el-not-badge">{t({ en: "60 minutes", ar: "60 دقيقة", ru: "60 минут" })}</span>
            <span className="el-not-badge">{t({ en: "In person", ar: "حضورياً", ru: "Лично" })}</span>
          </div>
          <div className="el-hero-actions">
            <a href={lp("/#contact")} className="btn btn-gold-lg">
              {t({ en: "Book a Discovery Call", ar: "احجز مكالمة تعريفية مجانية", ru: "Бесплатная консультация" })}
            </a>
            <button className="el-text-link" onClick={scrollToNext}>
              <span>{t({ en: "How it works", ar: "كيف يعمل البرنامج", ru: "Как это работает" })}</span>{ICON_ARROW_DOWN}
            </button>
          </div>
        </div>

        <div className="el-hero-right">
          <picture>
          <source srcSet={srcSetFor("/images/session-13")} sizes={SIZES.half} type="image/webp" />
          <img
            src="/images/session-13.jpg"
            alt={t({
              en: "A child at Kidsology working with a Japanese soroban abacus in a focused session",
              ar: "طفل في Kidsology يستخدم معداد السوروبان الياباني في جلسة تركيز",
              ru: "Ребёнок в Kidsology работает с японским соробаном на занятии, требующем концентрации",
            })}
            className="el-hero-photo"
            style={{ objectPosition: "center 55%" }}
          />
          </picture>
          <div className="el-hero-photo-wash" />
          <div className="el-hero-bracket" />
        </div>
      </header>

      {/* Trust strip */}
      <div className="el-trust">
        <div className="el-trust-item">{ICON_TROPHY} {t({ en: "Soroban World Champion coach", ar: "مدرب بطل العالم في السوروبان", ru: "Тренер, чемпион мира по соробану" })}</div>
        <span className="el-trust-sep" />
        <div className="el-trust-item">{ICON_CERT} {t({ en: "Japan accredited curriculum", ar: "منهج معتمد من اليابان", ru: "Программа, аккредитованная в Японии" })}</div>
        <span className="el-trust-sep" />
        <div className="el-trust-item">{ICON_PEOPLE} {t({ en: "Ages 6 to 10", ar: "من 6 إلى 10 سنوات", ru: "От 6 до 10 лет" })}</div>
        <span className="el-trust-sep" />
        <div className="el-trust-item">{ICON_PIN} {t({ en: "In person in Dubai", ar: "حضورياً في دبي", ru: "Лично в Дубае" })}</div>
      </div>

      <div className="prog-content">

        {/* ═══════════ I — WHAT IT IS ═══════════ */}
        <section className="el-section">
          <div className="el-inner">
            <span className="el-label reveal">I &nbsp;·&nbsp; {t({ en: "The programme", ar: "البرنامج", ru: "Программа" })}</span>
            <h2 className="el-h2 el-display reveal" style={{ "--d": "60ms" }}>
              {t({
                en: "A programme built on the original",
                ar: "برنامج مبني على طريقة",
                ru: "Программа на основе оригинального",
              })}
              <br />
              {t({
                en: "Japanese soroban method.",
                ar: "السوروبان اليابانية الأصلية.",
                ru: "японского метода соробан.",
              })}
            </h2>
            <p className="el-lead reveal" style={{ "--d": "120ms" }}>
              {t({
                en: "Your child learns to calculate with a real abacus, then to do mental math by picturing the soroban in their mind. That is the foundation of anzan. Japan has used this method to train focus and mental calculation in children for over a century, during the years the brain is still forming the architecture for lifelong concentration.",
                ar: "يتعلّم طفلك الحساب على معداد حقيقي، ثم الحساب الذهني عبر تصوّر السوروبان في ذهنه. هذا هو أساس الأنزان. تستخدم اليابان هذه الطريقة لتدريب التركيز والحساب الذهني عند الأطفال منذ أكثر من قرن، في السنوات التي لا يزال فيها الدماغ يبني بنية التركيز لمدى الحياة.",
                ru: "Ваш ребёнок учится считать на настоящем абакусе, а затем считать в уме, представляя соробан в голове. Это основа анзана. Япония использует этот метод для тренировки концентрации и устного счёта у детей более века, в те годы, когда мозг ещё формирует структуру концентрации на всю жизнь.",
              })}
            </p>

            <div className="el-subblock el-subblock--flush reveal" style={{ "--d": "180ms" }}>
              <div className="el-session-spec">
                {ICON_CLOCK} {t({ en: "60 minutes", ar: "60 دقيقة", ru: "60 минут" })} <i />{" "}
                <span>{t({ en: "In person in Dubai", ar: "حضورياً في دبي", ru: "Лично в Дубае" })}</span>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ II — HOW SESSIONS BUILD (NAVY) ═══════════ */}
        <section className="el-section el-plan">
          <div className="el-inner">
            <span className="el-label el-label--light reveal">II &nbsp;·&nbsp; {t({ en: "How sessions build", ar: "كيف تتطور الجلسات", ru: "Как строятся занятия" })}</span>
            <h2 className="el-h2 el-h2--light el-display reveal" style={{ "--d": "60ms" }}>
              {t({ en: "From beads in the hand", ar: "من الخرزات في اليد", ru: "От бусин в руке" })}
              <br />
              {t({ en: "to numbers in the mind.", ar: "إلى الأرقام في الذهن.", ru: "к числам в уме." })}
            </h2>
            <p className="el-lead el-lead--light reveal" style={{ "--d": "120ms" }}>
              {t({
                en: "Sessions follow the classical Japanese arc. Physical first, mental second, speed third. Focus and memory grow as a natural consequence.",
                ar: "تتبع الجلسات المسار الياباني الكلاسيكي. الحركة أولاً، الذهن ثانياً، السرعة ثالثاً. وينمو التركيز والذاكرة كنتيجة طبيعية.",
                ru: "Занятия идут по классической японской логике. Сначала руки, затем ум, затем скорость. Концентрация и память растут как естественное следствие.",
              })}
            </p>

            <div className="el-session-cards">
              {stages.map((c, i) => (
                <div key={i} className="el-scard reveal" style={{ "--d": `${i * 90}ms` }}>
                  <div className="el-scard-icon">{c.icon}</div>
                  <p className="el-scard-title">{c.title}</p>
                  <p className="el-scard-desc">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════ III — WHO + OUTCOMES ═══════════ */}
        <section className="el-section el-who">
          <div className="el-inner">
            <span className="el-label reveal">III &nbsp;·&nbsp; {t({ en: "For your child", ar: "لطفلك", ru: "Для вашего ребёнка" })}</span>
            <h2 className="el-h2 el-display reveal" style={{ "--d": "60ms" }}>
              {t({
                en: "Children aged 6 to 10, in the window",
                ar: "أطفال من 6 إلى 10 سنوات، في المرحلة",
                ru: "Дети от 6 до 10 лет, в тот период,",
              })}
              <br />
              {t({
                en: "where focus is still forming.",
                ar: "التي لا يزال فيها التركيز يتشكّل.",
                ru: "когда концентрация ещё формируется.",
              })}
            </h2>
            <p className="el-lead reveal" style={{ "--d": "120ms" }}>
              {t({
                en: "In a generation raised on screens, deep focus has become rare. This is the window to build it.",
                ar: "في جيل نشأ على الشاشات، صار التركيز العميق نادراً. وهذه هي المرحلة المناسبة لبنائه.",
                ru: "В поколении, выросшем на экранах, глубокая концентрация стала редкостью. Это подходящий период, чтобы её построить.",
              })}
            </p>

            <div className="el-twocol">
              <div className="el-col reveal">
                <p className="el-subhead">
                  {t({ en: "Who it is for", ar: "لمن هذا البرنامج", ru: "Кому подходит" })}
                </p>
                <div className="el-who-list el-who-list--single">
                  {whoItems.map((w, i) => (
                    <div key={i} className="el-who-item">
                      <span className="el-who-mark">{CHECK}</span>{w}
                    </div>
                  ))}
                </div>
              </div>
              <div className="el-col reveal" style={{ "--d": "120ms" }}>
                <p className="el-subhead">
                  {t({ en: "What your child will leave with", ar: "بماذا سيخرج طفلك", ru: "С чем ребёнок уйдёт" })}
                </p>
                <div className="el-benefits el-benefits--single">
                  {outcomes.map((o, i) => (
                    <div key={i} className="el-benefit">
                      <span className="el-benefit-check">{CHECK}</span>
                      <p className="el-benefit-text">{o}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ IV — TAUGHT BY ═══════════ */}
        <section className="el-section" style={{ paddingTop: 0 }}>
          <div className="el-inner">
            <span className="el-label reveal">IV &nbsp;·&nbsp; {t({ en: "Taught by", ar: "يقدّمه", ru: "Кто преподаёт" })}</span>
            <h2 className="el-h2 el-display reveal" style={{ "--d": "60ms" }}>
              {t({
                en: "The 2014 Soroban World Champion,",
                ar: "بطل العالم في السوروبان لعام 2014،",
                ru: "Чемпион мира по соробану 2014 года,",
              })}
              <br />
              {t({
                en: "delivering the Japanese curriculum in person.",
                ar: "يقدّم المنهج الياباني حضورياً.",
                ru: "который ведёт японскую программу лично.",
              })}
            </h2>

            <div className="el-session-cards" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
              <div className="el-scard reveal">
                <div className="el-scard-icon">{ICON_TROPHY}</div>
                <p className="el-scard-title">
                  {t({ en: "Head coach", ar: "المدرب الرئيسي", ru: "Главный тренер" })}
                </p>
                <p className="el-scard-desc">
                  {t({
                    en: "A Soroban World Champion leads the sessions in person, the way the method is taught in Japan.",
                    ar: "بطل عالم في السوروبان يقود الجلسات حضورياً، بالطريقة التي تُدرّس بها في اليابان.",
                    ru: "Чемпион мира по соробану ведёт занятия лично, так, как этот метод преподают в Японии.",
                  })}
                </p>
              </div>
              <div className="el-scard reveal" style={{ "--d": "120ms" }}>
                <div className="el-scard-icon">{ICON_CERT}</div>
                <p className="el-scard-title">
                  {t({ en: "Curriculum", ar: "المنهج", ru: "Программа" })}
                </p>
                <p className="el-scard-desc">
                  {t({
                    en: "Officially Japan accredited, KHDA and MOE approved for the 2026/27 academic year in Dubai.",
                    ar: "معتمد رسمياً من اليابان، ومعتمد من هيئة المعرفة والتنمية البشرية (KHDA) ووزارة التربية والتعليم للعام الدراسي 2026/27 في دبي.",
                    ru: "Официально аккредитована в Японии, одобрена KHDA и Министерством образования ОАЭ на 2026/27 учебный год в Дубае.",
                  })}
                </p>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Fixed scroll cue */}
      <button
        className={`ed-scroll-cue${nearBottom ? " ed-scroll-cue--hidden" : ""}`}
        onClick={scrollToNext}
        aria-label={t({ en: "Scroll down for more", ar: "انزل للمزيد", ru: "Прокрутите вниз" })}
      >
        <span className="ed-scroll-cue-text">
          {t({ en: "Learn more", ar: "اعرف المزيد", ru: "Подробнее" })}
        </span>
        <svg className="ed-scroll-cue-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Closing CTA */}
      <section className="el-cta">
        <div className="el-cta-inner">
          <h2 className="el-cta-title el-display">
            {t({ en: "Ready to start?", ar: "هل أنتم مستعدون للبدء؟", ru: "Готовы начать?" })}
          </h2>
          <p className="el-cta-sub">
            {t({
              en: "Book a free fifteen minute discovery call. We will listen, advise honestly, and tell you if this is the right fit for your child right now.",
              ar: "احجز مكالمة تعريفية مجانية لمدة خمس عشرة دقيقة. سنستمع، وننصح بصراحة، ونخبرك إن كان هذا مناسباً لطفلك الآن.",
              ru: "Запишитесь на бесплатную вводную консультацию на пятнадцать минут. Мы выслушаем, честно посоветуем и скажем, подходит ли это вашему ребёнку сейчас.",
            })}
          </p>
          <a href={lp("/#contact")} className="btn btn-gold-lg">
            {t({ en: "Book a Discovery Call", ar: "احجز مكالمة تعريفية مجانية", ru: "Бесплатная консультация" })}
          </a>
        </div>
      </section>
      </main>
    </div>
  );
}
